import { Dispatch, ReactNode, SetStateAction, useState } from 'react';
import {
	DndContext,
	DragEndEvent,
	DragOverlay,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';

export type DraggableContainerProps<T> = {
	list: T[];
	setList: Dispatch<SetStateAction<T[]>>;
	getItemId?: (item: T) => string;
	renderDragOverlay?: (activeId: string) => ReactNode;
	children: ReactNode;
};

const defaultGetItemId = <T extends object>(
	x: T & { uuid?: string; id?: string }
) => (x as { uuid?: string }).uuid ?? (x as { id?: string }).id ?? '';

export function DraggableContainer<T>({
	list,
	setList,
	getItemId = defaultGetItemId as (item: T) => string,
	renderDragOverlay,
	children,
}: DraggableContainerProps<T>) {
	const itemIds = list.map(getItemId);
	const [activeId, setActiveId] = useState<string | null>(null);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragStart = (event: { active: { id: unknown } }) => {
		setActiveId(String(event.active.id));
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setActiveId(null);
		if (!over || active.id === over.id) {
			return;
		}
		const oldIndex = itemIds.indexOf(String(active.id));
		const newIndex = itemIds.indexOf(String(over.id));
		if (oldIndex === -1 || newIndex === -1) {
			return;
		}
		setList(arrayMove(list, oldIndex, newIndex));
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={itemIds}
				strategy={verticalListSortingStrategy}
			>
				{children}
			</SortableContext>
			{renderDragOverlay && (
				<DragOverlay>
					{activeId ? renderDragOverlay(activeId) : null}
				</DragOverlay>
			)}
		</DndContext>
	);
}
